import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import api from "@/api/client";
import { Card, Streak, Coins, ShadowBadge, EmptyState, Spinner } from "@/components/ui";
import { getShadow } from "@/components/ui/ShadowBadge";
import XPBar, { XPRing, getLevel, getLevelProgress } from "@/components/ui/XPBar";
import Button from "@/components/ui/Button";
import Particles from "@/components/ui/Particles";
import clsx from "clsx";

/* ── Zeit-basierte Begrüßung + rotierende Subline ─────────────────────────── */
function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return { text: "Nachtschicht",   emoji: "🌙" };
  if (h < 11) return { text: "Guten Morgen",   emoji: "☀️" };
  if (h < 14) return { text: "Mahlzeit",       emoji: "🍽️" };
  if (h < 18) return { text: "Willkommen zurück", emoji: "⚡" };
  if (h < 22) return { text: "Guten Abend",    emoji: "✨" };
  return { text: "Noch wach?", emoji: "🌙" };
}
const SUBLINES = [
  "Bereit für neue XP?",
  "Dein Streak wartet auf dich.",
  "Kleine Schritte. Große Level.",
  "Heute schon gequizzt?",
  "Die Arena ruft.",
  "Wissen ist dein Superpower.",
  "Ein Quest pro Tag hält den Streak wach.",
];
const subline = () => {
  const day = Math.floor(Date.now() / 86400000);
  return SUBLINES[day % SUBLINES.length];
};

/* ── Count-up: Zahlen zählen beim Laden sichtbar hoch ─────────────────────── */
function useCountUp(target, dur = 1100) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const from = prev.current;
    prev.current = target;
    if (from === target) { setVal(target); return; }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setVal(target); return; }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * e));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, dur]);
  return val;
}

/* ── Streak-Flamme mit Stufen: 0 → grau, 3+ → Feuer, 7+ → cyan, 30+ → violett ─ */
function StreakFlame({ streak }) {
  const tier =
    streak >= 30 ? { c1: "#c084fc", c2: "#8b5cf6", label: "LEGENDÄR", glow: "rgba(192,132,252,0.65)" } :
    streak >= 7  ? { c1: "#67e8f9", c2: "#06b6d4", label: "HEISS",    glow: "rgba(103,232,249,0.6)"  } :
    streak >= 3  ? { c1: "#fbbf24", c2: "#f97316", label: "STREAK",   glow: "rgba(251,146,60,0.6)"   } :
    streak >= 1  ? { c1: "#fb923c", c2: "#ea580c", label: "STREAK",   glow: "rgba(251,146,60,0.35)"  } :
                   { c1: "#4b5563", c2: "#374151", label: "STREAK",   glow: "transparent" };
  const alive = streak >= 1;
  return (
    <div className="flex-shrink-0 flex flex-col items-center" title={`${streak} Tage Streak`}>
      <div className={clsx("relative", alive && "flame-flicker")}
        style={{ filter: alive ? `drop-shadow(0 0 10px ${tier.glow})` : "none" }}>
        <svg width="34" height="40" viewBox="0 0 24 28" fill="none">
          <defs>
            <linearGradient id="flame-g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={tier.c1} />
              <stop offset="1" stopColor={tier.c2} />
            </linearGradient>
          </defs>
          <path d="M12 1 C 13 6 18 8 18 15 A 6.5 6.5 0 0 1 5 15 C 5 10 9 9 9 5 C 10.5 6.5 11.5 8 11.5 10 C 12.5 8 12 4 12 1 Z"
            fill="url(#flame-g)" opacity={alive ? 1 : 0.5} />
          <path d="M12 12 C 13.5 15 15 15.5 15 18.5 A 3.2 3.2 0 0 1 8.6 18.5 C 8.6 16 11 15 12 12 Z"
            fill="#fff" opacity={alive ? 0.85 : 0.15} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center pt-2.5 text-[11px] font-black"
          style={{ color: alive ? "#1c1917" : "#9ca3af" }}>
          {streak}
        </span>
      </div>
      <div className="text-[9px] font-black tracking-widest mt-0.5" style={{ color: alive ? tier.c1 : "#6b7280" }}>
        {tier.label}
      </div>
    </div>
  );
}

/* ── Konfetti-Burst, wenn alle Daily Quests geschafft sind (1× pro Tag) ────── */
function QuestConfetti() {
  const COLORS = ["#8b5cf6", "#06b6d4", "#ec4899", "#fbbf24", "#4ade80", "#fff"];
  const pieces = useRef(
    Array.from({ length: 42 }, (_, i) => ({
      left: Math.random() * 100,
      size: 5 + Math.random() * 6,
      color: COLORS[i % COLORS.length],
      delay: Math.random() * 0.6,
      dur: 2.4 + Math.random() * 2,
    }))
  ).current;
  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 80 }} aria-hidden>
      {pieces.map((p, i) => (
        <div key={i} className="absolute rounded-sm"
          style={{
            left: `${p.left}%`, top: -16, width: p.size, height: p.size * 0.62, background: p.color,
            animation: `confetti-fall ${p.dur}s ease-in ${p.delay}s forwards`,
          }}
        />
      ))}
    </div>
  );
}

/* ── Quick action cards ────────────────────────────────────────────────────── */
const QUICK_ACTIONS = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18h6M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
      </svg>
    ),
    label: "Quiz starten",
    sub: "Wissen testen",
    path: "/quiz",
    from: "from-violet-700",
    to:   "to-cyan-600",
    glow: "rgba(139,92,246,0.5)",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
    label: "Arena",
    sub: "Multiplayer",
    path: "/arena",
    from: "from-pink-700",
    to:   "to-red-600",
    glow: "rgba(236,72,153,0.5)",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
    label: "Aufgaben",
    sub: "IRL Challenges",
    path: "/tasks",
    from: "from-cyan-700",
    to:   "to-blue-600",
    glow: "rgba(6,182,212,0.5)",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
    label: "Flow Tresor",
    sub: "Dateien lagern",
    path: "/vault",
    from: "from-amber-700",
    to:   "to-yellow-600",
    glow: "rgba(251,191,36,0.5)",
  },
];

/* ── Stat tile ─────────────────────────────────────────────────────────────── */
function StatTile({ label, value, icon, color = "text-violet-400" }) {
  return (
    <div className="flex-1 bg-nexus-s2 rounded-2xl p-3 text-center border border-nexus-border relative overflow-hidden group">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.06) 0%, transparent 70%)" }}
      />
      <div className={clsx("text-lg font-black leading-tight", color)}>{value}</div>
      <div className="text-[9px] text-gray-500 mt-0.5 font-bold uppercase tracking-widest">{label}</div>
    </div>
  );
}

/* ── Circular quest progress ───────────────────────────────────────────────── */
function QuestRing({ pct, done, size = 36 }) {
  const r    = size / 2 - 3;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90 flex-shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(139,92,246,0.12)" strokeWidth="2.5"/>
      <circle
        cx={size/2} cy={size/2} r={r} fill="none"
        stroke={done ? "#22c55e" : "url(#qr-grad)"}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circ}`}
        style={{ transition: "stroke-dasharray 0.7s cubic-bezier(0.22,1,0.36,1)" }}
      />
      <defs>
        <linearGradient id="qr-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8b5cf6"/>
          <stop offset="100%" stopColor="#06b6d4"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function Dashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data: studentData, isLoading } = useQuery({
    queryKey: ["student", "me"],
    queryFn:  () => api.get("/students/me").then((r) => r.data),
    refetchInterval: 60_000,
  });

  const { data: quizzesData } = useQuery({
    queryKey: ["quizzes"],
    queryFn:  () => api.get("/quizzes").then((r) => r.data),
  });

  const { data: dailyData } = useQuery({
    queryKey: ["daily"],
    queryFn:  () => api.get("/daily/quests").then((r) => r.data),
  });

  const { data: tasksData } = useQuery({
    queryKey: ["tasks"],
    queryFn:  () => api.get("/tasks").then((r) => r.data),
  });

  const { data: eventsData } = useQuery({
    queryKey: ["gamification-events"],
    queryFn:  () => api.get("/gamification/events").then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  const { data: convData } = useQuery({
    queryKey:        ["conversations"],
    queryFn:         () => api.get("/messages/conversations", { timeout: 8000 }).then(r => r.data),
    refetchInterval: 20_000,
    staleTime:       5_000,
    retry:           1,
  });
  const unreadConvos = (convData?.conversations || []).filter(c => c.unreadCount > 0);
  const totalUnread  = unreadConvos.reduce((s, c) => s + (c.unreadCount || 0), 0);

  const profile   = studentData?.student;
  const xp        = profile?.xp || 0;
  const coins     = profile?.coins || 0;
  const streak    = profile?.streak || 0;
  const level     = getLevel(xp);
  const shadow    = getShadow(xp);
  const quizzes   = quizzesData?.quizzes || [];
  const featured  = quizzes.filter((q) => !q.bestAttempt).slice(0, 3);
  const daily     = dailyData?.quests || [];
  const tasks     = (tasksData?.tasks || []).filter((t) => !t.completion).slice(0, 3);
  const doneToday = daily.filter((d) => d.progress?.completed).length;
  const questPct  = daily.length > 0 ? Math.round((doneToday / daily.length) * 100) : 0;

  // Count-up: XP & Coins zählen sichtbar hoch (Hooks vor dem Loading-Return!)
  const xpAnim    = useCountUp(xp);
  const coinsAnim = useCountUp(coins);
  const greeting  = getGreeting();

  // Konfetti-Feier, wenn alle Daily Quests geschafft sind — einmal pro Tag
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (daily.length === 0 || doneToday !== daily.length) return;
    const key = `flow_quest_confetti_${new Date().toISOString().slice(0, 10)}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch { /* private mode */ }
    setCelebrate(true);
    const t = setTimeout(() => setCelebrate(false), 5200);
    return () => clearTimeout(t);
  }, [doneToday, daily.length]);

  if (isLoading) {
    return (
      <div className="space-y-5 animate-fade-in" aria-busy="true" aria-label="Lädt dein Profil">
        {/* Hero skeleton */}
        <div className="rounded-2xl p-5 border border-nexus-border bg-nexus-s2/60 overflow-hidden">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full skel" />
            <div className="flex-1 space-y-2.5">
              <div className="h-3 w-24 rounded-full skel" />
              <div className="h-5 w-40 rounded-full skel" />
              <div className="h-3 w-28 rounded-full skel" />
            </div>
            <div className="w-12 h-12 rounded-xl skel" />
          </div>
          <div className="flex gap-2 mt-5">
            <div className="flex-1 h-14 rounded-2xl skel" />
            <div className="flex-1 h-14 rounded-2xl skel" />
            <div className="flex-1 h-14 rounded-2xl skel" />
          </div>
        </div>
        {/* Quest skeletons */}
        <div className="space-y-2">
          <div className="h-4 w-32 rounded-full skel" />
          <div className="h-16 rounded-2xl skel" />
          <div className="h-16 rounded-2xl skel" />
        </div>
        {/* Quick-action skeleton */}
        <div className="grid grid-cols-2 gap-2.5">
          <div className="h-24 rounded-2xl skel" />
          <div className="h-24 rounded-2xl skel" />
          <div className="h-24 rounded-2xl skel" />
          <div className="h-24 rounded-2xl skel" />
        </div>
      </div>
    );
  }

  // Parent alert: check if student has no linked parents
  const linkedParents = profile?.parents || [];
  const showParentAlert = linkedParents.length === 0;

  // Active seasonal events
  const now = new Date();
  const activeEvents = (eventsData?.events || []).filter(
    (ev) => ev.isActive && new Date(ev.startsAt) <= now && new Date(ev.endsAt) >= now
  );

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Feier: alle Daily Quests geschafft ─────────────────────────────── */}
      {celebrate && <QuestConfetti />}

      {/* ── PARENT SETUP ALERT ─────────────────────────────────────────────── */}
      {showParentAlert && (
        <button
          onClick={() => navigate("/settings")}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] group"
          style={{
            background: "linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(217,119,6,0.04) 100%)",
            borderColor: "rgba(245,158,11,0.35)",
          }}
        >
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#fbbf24"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-amber-300">Eltern noch nicht eingerichtet</p>
            <p className="text-[10px] text-amber-600/80 mt-0.5">Verknüpfe bis zu 2 Elternteile — Einstellungen öffnen</p>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#fbbf24"
            strokeLinecap="round" strokeLinejoin="round"
            className="flex-shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      )}

      {/* ── SEASONAL EVENT BANNER ─────────────────────────────────────────── */}
      {activeEvents.length > 0 && activeEvents.map((ev) => {
        const msLeft   = new Date(ev.endsAt) - new Date();
        const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
        const claimable = (ev.challenges || []).filter((c) => c.myProgress?.completed && !c.myProgress?.rewardClaimed).length;
        return (
          <button
            key={ev.id}
            onClick={() => navigate("/events")}
            className="w-full relative rounded-2xl border overflow-hidden text-left hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 group"
            style={{
              borderColor: `${ev.bannerColor}50`,
              background: `linear-gradient(135deg, ${ev.bannerColor}18 0%, rgba(13,13,43,0.85) 55%)`,
              boxShadow: `0 4px 24px ${ev.bannerColor}20`,
            }}
          >
            <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: `linear-gradient(90deg,transparent,${ev.bannerColor},transparent)` }}/>
            <div className="p-4 flex items-center gap-3">
              <span className="text-3xl" style={{ filter: `drop-shadow(0 0 10px ${ev.bannerColor}80)` }}>{ev.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-black text-sm">{ev.name}</p>
                  {ev.xpMultiplier > 1 && (
                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md"
                      style={{ background: `${ev.bannerColor}25`, color: ev.bannerColor, border: `1px solid ${ev.bannerColor}40` }}>
                      ×{ev.xpMultiplier} XP
                    </span>
                  )}
                  {claimable > 0 && (
                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md bg-green-500/20 text-green-400 border border-green-500/40 animate-pulse">
                      {claimable}× einlösbar!
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {daysLeft === 0 ? "Endet heute" : `Noch ${daysLeft} Tag${daysLeft !== 1 ? "e" : ""}`}
                  {" · "}{(ev.challenges||[]).filter((c)=>c.myProgress?.completed).length}/{(ev.challenges||[]).length} Challenges
                </p>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke={ev.bannerColor}
                strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </div>
          </button>
        );
      })}

      {/* ── HERO CARD ──────────────────────────────────────────────────────── */}
      <Card glow aurora className="p-5">
        <Particles n={18} />

        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-5">

            {/* XP Ring + Avatar combo */}
            <div className="relative flex-shrink-0">
              {/* slow conic halo orbiting behind the ring — the hero's aura */}
              <div className="hero-ring absolute -inset-2 rounded-full opacity-60 pointer-events-none" />
              <XPRing xp={xp} size={80} />
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: "transparent" }}
              >
                {/* Avatar overlays on top of ring label – handled by XPRing internally */}
              </div>
              {/* Avatar floats outside ring */}
              <div
                className="absolute -top-2 -right-2 w-10 h-10 rounded-2xl bg-nexus-s2 border-2 border-violet-500/50 flex items-center justify-center text-2xl shadow-flow animate-float"
                style={{ filter: "drop-shadow(0 0 8px rgba(139,92,246,0.4))" }}
              >
                {user?.avatar || "🦊"}
              </div>
            </div>

            {/* Name + badge */}
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest">{greeting.text} {greeting.emoji}</p>
              <h2 className="text-xl font-black truncate mt-0.5">
                {user?.name?.split(" ")[0]}
                <span className="text-violet-400"> ⚡</span>
              </h2>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <ShadowBadge xp={xp} />
                <span className="text-[10px] text-gray-500 italic hidden sm:inline">{subline()}</span>
              </div>
            </div>

            {/* Streak — lebendige Flamme mit Stufen */}
            <StreakFlame streak={streak} />
          </div>

          {/* Stat row — Zahlen zählen beim Laden hoch */}
          <div className="flex gap-2">
            <StatTile
              label="Coins"
              value={
                <span className="flex items-center justify-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#fbbf24" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M9 9h4.5a1.5 1.5 0 0 1 0 3H9m0 3h5.5a1.5 1.5 0 0 0 0-3"/></svg>
                  {coinsAnim.toLocaleString("de-DE")}
                </span>
              }
              color="text-yellow-400"
            />
            <StatTile
              label="Gesamt XP"
              value={<span>⚡ {xpAnim.toLocaleString("de-DE")}</span>}
              color="text-violet-400"
            />
            <StatTile
              label="Quizze"
              value={profile?.quizAttempts?.length || 0}
              color="text-cyan-400"
            />
          </div>

          {/* Level-Fortschritt — wie weit bis zum nächsten Level? */}
          <div className="mt-3">
            <XPBar xp={xp} size="sm" />
          </div>

          {/* Quest progress mini bar */}
          {daily.length > 0 && (
            <div className="mt-3 flex items-center gap-2.5 p-2.5 bg-nexus-s2 rounded-xl border border-nexus-border">
              <div className="flex-1">
                <div className="flex justify-between text-[10px] font-bold mb-1">
                  <span className="text-gray-400">⚔️ Daily Quests</span>
                  <span className="text-violet-400">{doneToday}/{daily.length}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(139,92,246,0.1)" }}>
                  <div
                    className="h-full xp-bar rounded-full transition-all duration-700"
                    style={{ width: `${questPct}%` }}
                  />
                </div>
              </div>
              {doneToday === daily.length && (
                <span className="text-green-400 text-lg flex-shrink-0">✅</span>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* ── DAILY QUESTS ───────────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-sm flex items-center gap-2">
            <span className="text-orange-400">⚔️</span>
            <span>Daily Quests</span>
          </h3>
          <span className="text-[11px] font-bold text-gray-500 bg-nexus-s2 px-2.5 py-1 rounded-full border border-nexus-border">
            {doneToday}/{daily.length}
          </span>
        </div>

        {daily.length === 0 ? (
          <Card className="p-5">
            <EmptyState icon="⚔️" title="Keine Quests heute" desc="Schau morgen wieder rein!" />
          </Card>
        ) : (
          <div className="space-y-2">
            {daily.map((q) => {
              const done = q.progress?.completed;
              const cur  = q.progress?.current || 0;
              const pct  = Math.min((cur / q.target) * 100, 100);
              return (
                <Card key={q.id} className={clsx("p-3.5 transition-all duration-300", done && "opacity-55")}>
                  <div className="flex items-center gap-3">
                    <QuestRing pct={pct} done={done} />
                    <span className="text-xl flex-shrink-0 -ml-1">{q.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={clsx("text-sm font-bold truncate", done && "line-through text-gray-500")}>{q.title}</p>
                        <div className="flex gap-1.5 flex-shrink-0 ml-2">
                          <span className="text-[10px] font-black text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded-md">+{q.xpReward}XP</span>
                          <span className="text-[10px] font-black text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded-md">+{q.coinsReward}🪙</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-600 mt-0.5">{cur}/{q.target} abgeschlossen</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* ── NACHRICHTEN CARD ───────────────────────────────────────────────── */}
      <button
        onClick={() => navigate("/messages")}
        className="w-full text-left rounded-2xl p-4 border transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
        style={{
          background: totalUnread > 0
            ? "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(6,182,212,0.08))"
            : "rgba(255,255,255,0.03)",
          border: totalUnread > 0
            ? "1px solid rgba(139,92,246,0.35)"
            : "1px solid rgba(255,255,255,0.07)",
          boxShadow: totalUnread > 0 ? "0 0 24px rgba(139,92,246,0.12)" : "none",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.25)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-violet-500 text-white text-[9px] font-black rounded-full flex items-center justify-center px-0.5"
                style={{ boxShadow: "0 0 10px rgba(139,92,246,0.6)" }}>
                {totalUnread > 9 ? "9+" : totalUnread}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-black text-sm text-white">Nachrichten</p>
            <p className="text-xs mt-0.5" style={{ color: totalUnread > 0 ? "#c4b5fd" : "rgba(255,255,255,0.4)" }}>
              {totalUnread > 0
                ? `${totalUnread} ungelesene Nachricht${totalUnread !== 1 ? "en" : ""}`
                : "Schreib deinem Lehrer oder Mentor"}
            </p>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="rgba(255,255,255,0.3)" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>
      </button>

      {/* ── QUICK ACTIONS ──────────────────────────────────────────────────── */}
      <section>
        <h3 className="font-black text-sm mb-3 flex items-center gap-2">
          <span className="text-cyan-400">⚡</span>
          <span>Schnellstart</span>
        </h3>
        <div className="grid grid-cols-2 gap-2.5">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.path}
              onClick={() => navigate(a.path)}
              className={clsx(
                `bg-gradient-to-br ${a.from} ${a.to}`,
                "rounded-2xl p-4 text-left relative overflow-hidden group",
                "hover:-translate-y-1 hover:scale-[1.02] active:scale-[0.98]",
                "transition-all duration-250",
              )}
              style={{
                boxShadow: `0 4px 20px ${a.glow.replace('0.5', '0.25')}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 8px 32px ${a.glow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = `0 4px 20px ${a.glow.replace('0.5', '0.25')}`;
              }}
            >
              {/* Shine sweep */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 50%)" }}
              />
              <div className="relative z-10">
                <div className="text-white mb-2 opacity-90 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3 origin-left">{a.icon}</div>
                <p className="font-black text-sm text-white leading-tight">{a.label}</p>
                <p className="text-xs text-white/65 mt-0.5">{a.sub}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── FEATURED QUIZZES ───────────────────────────────────────────────── */}
      {featured.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black text-sm flex items-center gap-2">
              <span className="text-violet-400">📚</span>
              <span>Für dich empfohlen</span>
            </h3>
            <button onClick={() => navigate("/quiz")} className="text-xs text-violet-400 font-bold hover:text-violet-300 transition-colors flex items-center gap-1">
              Alle
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
          <div className="space-y-2">
            {featured.map((q) => (
              <Card key={q.id} hover onClick={() => navigate(`/quiz/${q.id}`)} className="p-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-nexus-s2 border border-nexus-border flex items-center justify-center text-xl flex-shrink-0">
                    {q.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{q.title}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{q.category} · {["","⭐","⭐⭐","⭐⭐⭐","⭐⭐⭐⭐","⭐⭐⭐⭐⭐"][q.difficulty]}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-black text-violet-400">+{q.xpReward} XP</p>
                    <p className="text-[10px] text-gray-600">{q._count?.attempts || 0}× gespielt</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* ── OPEN TASKS ─────────────────────────────────────────────────────── */}
      {tasks.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black text-sm flex items-center gap-2">
              <span className="text-cyan-400">📌</span>
              <span>Offene Aufgaben</span>
            </h3>
            <button onClick={() => navigate("/tasks")} className="text-xs text-cyan-400 font-bold hover:text-cyan-300 transition-colors flex items-center gap-1">
              Alle
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
          <div className="space-y-2">
            {tasks.map((t) => (
              <Card key={t.id} hover onClick={() => navigate("/tasks")} className="p-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-nexus-s2 border border-nexus-border flex items-center justify-center text-xl flex-shrink-0">
                    {t.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{t.title}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{t.type === "irl" ? "🌍 IRL-Aufgabe" : "💻 Digital"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-black text-green-400">+{t.xpReward} XP</p>
                    <p className="text-[10px] text-yellow-400">+{t.coinsReward}🪙</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      <div className="pb-2" />
    </div>
  );
}
